import { useState } from "react";
import { Twitter, Facebook, Linkedin, Link2, Send, Check } from "lucide-react";

type Props = {
  title: string;
  text?: string;
  /** Optional hash appended to current URL, e.g. "#regions" */
  hash?: string;
};

function getShareUrl(hash?: string) {
  if (typeof window === "undefined") return "";
  const { origin, pathname } = window.location;
  return `${origin}${pathname}${hash ?? ""}`;
}

export default function ShareButtons({ title, text, hash }: Props) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const url = getShareUrl(hash);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* noop */
    }
  };

  const handleNative = async () => {
    const url = getShareUrl(hash);
    if (typeof navigator !== "undefined" && (navigator as any).share) {
      try {
        await (navigator as any).share({ title, text: text ?? title, url });
        return;
      } catch {
        /* user cancelled */
      }
    }
    handleCopy();
  };

  const url = getShareUrl(hash);
  const enc = encodeURIComponent;
  const links = [
    {
      key: "x",
      label: "X",
      Icon: Twitter,
      href: `https://twitter.com/intent/tweet?text=${enc(title)}&url=${enc(url)}`,
    },
    {
      key: "fb",
      label: "Facebook",
      Icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
    },
    {
      key: "in",
      label: "LinkedIn",
      Icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`,
    },
  ];

  return (
    <div className="border-t border-border pt-2.5 mt-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">
          Share this region
        </span>
        <div className="flex items-center gap-1">
          {links.map(({ key, label, Icon, href }) => (
            <a
              key={key}
              href={href}
              target="_blank"
              rel="noreferrer"
              aria-label={`Share on ${label}`}
              title={`Share on ${label}`}
              className="p-1.5 border border-border hover:border-hvi-primary hover:text-hvi-primary text-muted-foreground transition"
            >
              <Icon className="w-3.5 h-3.5" />
            </a>
          ))}
          <button
            onClick={handleNative}
            aria-label="Share"
            title="Share"
            className="p-1.5 border border-border hover:border-hvi-primary hover:text-hvi-primary text-muted-foreground transition md:hidden"
          >
            <Send className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={handleCopy}
            aria-label="Copy link"
            title="Copy link"
            className={`p-1.5 border transition ${
              copied
                ? "border-hvi-primary text-hvi-primary"
                : "border-border hover:border-hvi-primary hover:text-hvi-primary text-muted-foreground"
            }`}
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
    </div>
  );
}
